import { useCallback, useMemo } from "react";
import {
  Button,
  Group,
  Paper,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import type { LegacyReviewForm } from "../../../utils/legacyReviewForm";
import LegacySlideFields from "./LegacySlideFields";

interface Props {
  form: LegacyReviewForm;
  index: number;
}

export default function LegacySpecimenFields({ form, index }: Props) {
  const path = useMemo(() => `specimens.${index}`, [index]);
  const slides = useMemo(
    () => form.values.specimens[index].completedSlides ?? [],
    [form.values.specimens, index]
  );
  const handleRemove = useCallback(
    () => form.removeListItem("specimens", index),
    [form, index]
  );
  const handleAddSlide = useCallback(
    () =>
      form.setFieldValue(`${path}.completedSlides`, [
        ...slides,
        { number: "", evidenceText: "" },
      ]),
    [form, path, slides]
  );
  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>Specimen {index + 1}</Title>
          <Button variant="subtle" color="red" onClick={handleRemove}>
            Remove specimen
          </Button>
        </Group>
        <TextInput
          label="Specimen number"
          withAsterisk
          aria-required
          {...form.getInputProps(`${path}.number`)}
        />
        <Textarea
          label="Exact specimen evidence from report"
          withAsterisk
          aria-required
          autosize
          minRows={3}
          {...form.getInputProps(`${path}.evidenceText`)}
        />
        <Textarea
          label="Specimen gross description"
          autosize
          minRows={2}
          {...form.getInputProps(`${path}.grossText`)}
        />
        <Textarea
          label="Specimen diagnosis"
          autosize
          minRows={2}
          {...form.getInputProps(`${path}.diagnosisText`)}
        />
        <Textarea
          label="Specimen microscopic description"
          autosize
          minRows={2}
          {...form.getInputProps(`${path}.microscopicText`)}
        />
        {slides.map((slide, slideIndex) => (
          <LegacySlideFields
            key={slideIndex}
            form={form}
            specimenIndex={index}
            slideIndex={slideIndex}
          />
        ))}
        <Button variant="light" onClick={handleAddSlide}>
          Add completed slide
        </Button>
      </Stack>
    </Paper>
  );
}
